import React, { ChangeEvent, useState, useEffect, useRef } from 'react';
import { TextField } from '@care/react-component-lib';
import { Box, Button, InputAdornment } from '@mui/material';
import { Icon24InfoLocation } from '@care/react-icons';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import { useSearchDirectoryData } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';

interface ZipCodeConversionProps {
  showZipCodeInputField: boolean;
  isDirectoryFirstExperience?: boolean;
  handleCtaBtn: () => void;
  ctaText: string;
}

export const ZipCodeConversion = ({
  showZipCodeInputField,
  isDirectoryFirstExperience = false,
  ctaText,
  handleCtaBtn,
}: ZipCodeConversionProps) => {
  const {
    seoSegmentationDetails: { pageType },
  } = useSearchDirectoryData();
  const { enrollmentURL, userGeoInfo } = usePageAttributes();

  const zipInputRef = useRef<HTMLInputElement | null>(null);

  const prefillZipCode = (): string => {
    if (pageType === SEOSegmentationPageType.NATIONAL) {
      return getZipcodeFromGeoInfo(userGeoInfo);
    }
    return '';
  };

  const zipCodeRegex = /^\d{0,5}$/;
  const prefilledZipCode: string = prefillZipCode();
  const [zip, setZip] = useState({
    zipCode: prefilledZipCode,
  });

  useEffect(() => {
    if (zipInputRef.current) {
      zipInputRef.current.setSelectionRange(
        zipInputRef.current.value.length,
        zipInputRef.current.value.length
      );

      zipInputRef.current.focus();
    }
  }, [zip]);

  const enrollmentLink = (() => {
    if (zip.zipCode && zip.zipCode.length === 5) {
      return `${enrollmentURL}&zipcode=${zip.zipCode}`;
    }
    if (isDirectoryFirstExperience) {
      return `${enrollmentURL}&zipcode=${getZipcodeFromGeoInfo(userGeoInfo)}`;
    }
    return `${enrollmentURL}`;
  })();

  const onZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const zipCodeVal = e.target.value;

    if (!zipCodeRegex.test(zipCodeVal)) {
      return;
    }
    setZip((prevValues) => ({
      ...prevValues,
      zipCode: zipCodeVal,
    }));
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      globalThis.location.href = `${enrollmentLink}`;
      handleCtaBtn();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: 4,
      }}>
      {showZipCodeInputField && (
        <TextField
          id="zipCode"
          name="zipCode"
          inputRef={zipInputRef}
          onKeyUp={handleKeyUp}
          placeholder="ZIP code"
          value={zip.zipCode}
          onChange={onZipCodeChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 2 }}>
                <Icon24InfoLocation />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            width: { xs: '100%', sm: '329px', md: '369px' },

            '& .MuiInputAdornment-root': {
              ml: 0,
            },

            '& .MuiInputBase-root': {
              minHeight: '62px',
            },

            '& input[type=text]': {
              color: 'text.secondary',
              fontSize: 'body2.fontSize',
              lineHeight: 'body2.lineHeight',
              pb: 0,
            },
          }}
        />
      )}
      <Button
        color="primary"
        href={enrollmentLink}
        onClick={handleCtaBtn}
        size="large"
        variant="contained"
        rel="nofollow"
        sx={{
          width: { xs: '100%', md: '369px' },
        }}>
        {ctaText}
      </Button>
    </Box>
  );
};
